<template>
  <div class="home">
    <template v-if="!nucleusUser()">
      <h1 class="page-header">No Access</h1>
      You do not have access to requests. Please contact HIA.
    </template>
    <template v-else>
    <el-row class="header-row">
      <el-col :span="16">
        <el-breadcrumb separator="/">
          <el-breadcrumb-item>
            <clientNavSelector></clientNavSelector>
          </el-breadcrumb-item>
          <el-breadcrumb-item :to="{ path: '/requests' }">Requests</el-breadcrumb-item>
        </el-breadcrumb>
      </el-col>
      <el-col :span="8">
        <requestTraining style="float: right;"></requestTraining>
      </el-col>
    </el-row>
    <hia-loading v-if="loading"></hia-loading>
    <div v-show="!loading" class="top-margin">
      <el-row style="margin: 30px 0px 0px 0px;" :gutter="10" type="flex" justify="space-between">
        <el-col :span="8">
          <div style="height: 180px; position: relative;">
            <el-card shadow="hover" class="dataCard codingCard">
              <div slot="header">
                <span>Monthly/PRN Coding Support Request</span>
              </div>
              <div style="font-size: 14px;">
                Create a Monthly/PRN Coding Support Request for a specific month while selecting patient types and number of coders requested per day.
              </div>
              <div style="text-align: center; position: absolute; bottom: 15px; width: 90%;">
                <router-link :to="`/${currentClientId}/requests/new-coding-request`">
                  <el-button type="primary" icon="el-icon-plus" class="addRequestButton">New Monthly/PRN Coding Support Request </el-button>
                </router-link>
              </div>
            </el-card>
          </div>
        </el-col>
        <el-col :span="8">
          <div style="height: 180px; position: relative;">
            <el-card shadow="hover" class="dataCard reviewCard">
              <div slot="header">
                <span>Review/Audit Request</span>
              </div>
              <div style="font-size: 14px;">
                Create a Coding Review/Audit request for a specific date range and scope for Acute, Professional Fee, and/or Ambulatory Surgery Center records.
              </div>
              <div style="text-align: center; position: absolute; bottom: 15px; width: 90%;">
                <router-link :to="`/${currentClientId}/requests/new-review-request`">
                  <el-button type="primary" icon="el-icon-plus" class="addRequestButton">New Review/Audit Request </el-button>
                </router-link>
              </div>
            </el-card>
          </div>
        </el-col>
        <el-col :span="8">
          <div style="height: 180px; position: relative;">
            <el-card shadow="hover" class="dataCard interimCard">
              <div slot="header">
                <span>Interim Services Request</span>
              </div>
              <div style="font-size: 14px;">
                Create an Interim Services/Prebill Audit request for a specific date range and number of consultants for Acute and/or Professional Fee records.
              </div>
              <div style="text-align: center; position: absolute; bottom: 15px; width: 90%;">
                <router-link :to="`/${currentClientId}/requests/new-interim-request`">
                  <el-button type="primary" icon="el-icon-plus" class="addRequestButton">New Interim Services Request </el-button>
                </router-link>
              </div>
            </el-card>
          </div>
        </el-col>
      </el-row>
      <el-row style="margin: 30px 0px 0px 0px;">
        <el-col :span="24">
          <el-select style="float: right; margin-top: 10px;" v-model="selectedYear" placeholder="Select" @change="handleYearChange">
            <el-option v-for="item in yearDropdown" :key="item.key" :label="item.label" :value="item.key"> </el-option>
          </el-select>
          <el-table id="requestTable" max-height="400" :data="allRequests" style="width: 100%; margin-top: 20px;" label="Start Date">
            <el-table-column prop="requestType" label="Request Type" :filter-method="filterHandler" :filters="[{ text: 'Monthly/PRN Coding Support', value: 'Monthly/PRN Coding Support' }, { text: 'Coding Review/Audit', value: 'Coding Review/Audit' }, { text: 'Interim Services', value: 'Interim Services' }]"> </el-table-column>
            <el-table-column label="Date Range">
              <template slot-scope="scope">
                <div v-if="scope.row.requestType === 'Monthly/PRN Coding Support'">
                  <router-link :to="(scope.row.codingType === 'Acute' ? `/${currentClientId}/requests/coding-request/acute/` : `/${currentClientId}/requests/coding-request/pro/`) + scope.row.requestId"> {{ getMonthYear(scope.row.requestStartDate) }}</router-link>
                </div>
                <div v-if="scope.row.requestType === 'Coding Review/Audit'">
                  <router-link :to="`/${currentClientId}/requests/review-request/${scope.row.requestId}`"> {{ scope.row.dateRange }}</router-link>
                </div>
                <div v-if="scope.row.requestType === 'Interim Services'">
                  <router-link :to="`/${currentClientId}/requests/interim-request/${scope.row.requestId}`"> {{ scope.row.dateRange }}</router-link>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Service Line">
              <template slot-scope="scope">
                <div v-if="scope.row.requestType === 'Monthly/PRN Coding Support'">
                  {{ scope.row.codingType }}
                </div>
                <div v-if="scope.row.requestType === 'Coding Review/Audit'">
                  {{ scope.row.reviewType }}
                </div>
                <div v-if="scope.row.requestType === 'Interim Services'">
                  {{ scope.row.interimType }}
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Quantity">
              <template slot-scope="scope">
                <div v-if="scope.row.requestType === 'Monthly/PRN Coding Support'">
                  {{ `${scope.row.numberOfCoders} coders` }}
                </div>
                <div v-if="scope.row.requestType === 'Coding Review/Audit'">
                  {{ `${scope.row.numberOfRecords} records` }}
                </div>
                <div v-if="scope.row.requestType === 'Interim Services'">
                  {{ `${scope.row.numberConsultants} consultants` }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="reviewDescription" label="Review Objectives">
              <template slot-scope="scope">
                {{ scope.row.reviewDescription ? scope.row.reviewDescription : '' }}
              </template>
            </el-table-column>
            <el-table-column label="Created By/Updated By">
              <template slot-scope="scope">
                {{ scope.row | requestContactDisplay }}
              </template>
            </el-table-column>
            <el-table-column label="Created/Updated">
              <template slot-scope="scope">
                {{ scope.row | requestDateDisplay }}
              </template>
            </el-table-column>
            <template slot="empty">
              <p>No Requests Available</p>
            </template>
          </el-table>
        </el-col>
      </el-row>
    </div>
    </template>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { mapState, mapActions } from 'vuex'
import HiaAuthorization from '@/mixins/hiaAuthorization'
import { getRequestContactDisplay, getRequestDateDisplay } from '@/utilities/requestAuditDisplay'
import requestTraining from '@/views/Requests/requestTraining.vue'

export default {
  mixins: [HiaAuthorization],
  filters: {
    requestContactDisplay: getRequestContactDisplay,
    requestDateDisplay: getRequestDateDisplay
  },
  data: function() {
    return {
      loading: true,
      activeTab: 'coding',
      labels: {
        coding: 'Monthly/PRN Coding Support',
        review: 'Coding Review/Audit',
        interim: 'Interim Services'
      },
      selectedYear: new Date().getFullYear(),
      format: format,
      parseISO: parseISO
    }
  },
  name: 'requests',
  components: {
    requestTraining
  },
  methods: {
    ...mapActions('requests/', ['GET_REQUESTS', 'GET_ALL_REQUESTS']),
    initialize() {
      if (!this.nucleusUser()) {
        this.loading = false
        return
      }
      this.loading = true
      this.GET_REQUESTS(this.selectedYear).then(() => {
        this.loading = false
      })
      this.GET_ALL_REQUESTS(this.selectedYear)
    },
    handleYearChange() {
      if (!this.nucleusUser()) {
        return
      }
      this.loading = true
      this.GET_REQUESTS(this.selectedYear).then(() => {
        this.loading = false
      })
      this.GET_ALL_REQUESTS(this.selectedYear)
    },
    getMonthYear(isoDate) {
      let d = parseISO(isoDate)
      return `${d.toLocaleString('en-us', { month: 'long' })} ${d.getFullYear()}`
    },
    handleTabClick(tab, event) {
      switch (tab.name) {
        case 'coding':
          this.$router.replace(`/${this.currentClientId}/requests/coding`)
          break
        case 'review':
          this.$router.replace(`/${this.currentClientId}/requests/review`)
          break
        case 'interim':
          this.$router.replace(`/${this.currentClientId}/requests/interim`)
          break
      }
    },
    activeTabChanger(route) {
      this.activeTab = route.match(/\/([^/]+)\/?$/)[1]
    },
    getLabelForActiveTab(activeTab) {
      if (!this.loading) {
        return document.getElementById('tab-' + activeTab).innerHTML
      } else {
        return ''
      }
    },
    filterHandler(value, row, column) {
      const property = column['property']
      return row[property] === value
    }
  },
  created: function() {
    this.activeTabChanger(this.$route.fullPath)
    this.initialize()
  },
  computed: {
    ...mapState('user/', ['currentClient', 'currentClientId']),
    ...mapState('requests/', ['codingScheduleRequests', 'reviewRequests', 'interimRequests', 'allRequests']),
    yearDropdown() {
      let currentYear = new Date().getFullYear() + 1
      let years = []
      for (let i = currentYear; i > 2017; i--) {
        years.push({
          key: i,
          label: i
        })
      }
      return years
    }
  }
}
</script>

<style scoped>
.requestButton {
  margin-top: 5px;
  position: absolute;
  float: right;
  right: 13px;
  z-index: 1;
}

.addRequestButton {
  text-transform: capitalize;
}

>>> .dataCard {
  width: 100%;
  height: 100%;
}

>>> .codingCard .el-card__header {
  padding: 8px 10px;
  font-size: 18px;
  font-weight: 400;
  color: #ffffff;
  background: rgba(255, 196, 12, 0.8);
}

>>> .reviewCard .el-card__header {
  padding: 8px 10px;
  font-size: 18px;
  font-weight: 400;
  color: #ffffff;
  background: rgba(236, 0, 140, 0.8);
}

>>> .interimCard .el-card__header {
  padding: 8px 10px;
  font-size: 18px;
  font-weight: 400;
  color: #ffffff;
  background: rgba(51, 120, 170, 0.8);
}

>>> .el-table .cell {
  word-break: break-word;
}
</style>
